import { useFormContext } from 'react-hook-form';
import { Button, Upload } from 'antd';
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import toastMsg from '../../../lib/toastMsg';
import { ALIMTALK_CSV_LIMIT } from '../../../constants/service/app/alimtalkConstants';
import { AlimtalkSendFormValues } from '../../../types/alimtalk/form';
import * as Styled from './style';

export type CsvUploadSectionProps = {
  onUpload: (file: File) => void;
  isUploading: boolean;
  /** CSV 모드 여부 — 샘플 CSV 버튼은 CSV 모드 전용(그 외 백엔드 400), 헤더 안내 문구도 분기 */
  isCsvMode: boolean;
  onDownloadSample: () => void;
  isSampleDownloading: boolean;
};

const MAX_FILE_SIZE_MB = ALIMTALK_CSV_LIMIT.MAX_FILE_SIZE_BYTE / (1024 * 1024);

export const CsvUploadSection = (props: CsvUploadSectionProps) => {
  const { onUpload, isUploading, isCsvMode, onDownloadSample, isSampleDownloading } = props;
  const { watch } = useFormContext<AlimtalkSendFormValues>();
  const csvS3Key = watch('csvS3Key');
  const csvFileName = watch('csvFileName');

  // beforeUpload에서 false를 반환해 antd 자동 업로드를 막고, presigned 업로드 플로우로 직접 처리한다.
  // (행 수 검증은 클라이언트 파싱 비용이 커 Worker/preview에 위임 — 여기선 용량만 사전 차단)
  const handleBeforeUpload = (file: File): boolean => {
    if (file.size > ALIMTALK_CSV_LIMIT.MAX_FILE_SIZE_BYTE) {
      toastMsg.error(`CSV 파일은 ${MAX_FILE_SIZE_MB}MB 이하만 업로드할 수 있습니다.`);
      return false;
    }
    onUpload(file);
    return false;
  };

  return (
    <Styled.Section>
      <Styled.SectionTitleRow>
        <Styled.SectionTitle>발송 대상 CSV<Styled.RequiredMark>*</Styled.RequiredMark></Styled.SectionTitle>
        {isCsvMode && (
          <Button size="small" icon={<DownloadOutlined />} loading={isSampleDownloading} onClick={onDownloadSample}>
            샘플 CSV
          </Button>
        )}
      </Styled.SectionTitleRow>
      <Upload.Dragger accept=".csv" maxCount={1} showUploadList={false} beforeUpload={handleBeforeUpload}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">CSV 파일을 드래그하거나 클릭하여 업로드</p>
        <p className="ant-upload-hint">
          {/* 백엔드 §6.2.2 — CSV 모드: userId+변수 헤더 필수 / ACTION 모드: 헤더 없는 userId 1열 */}
          {isCsvMode ? 'userId + 변수 컬럼 헤더 필수' : 'userId 1열 (헤더 없음)'} · 최대 {MAX_FILE_SIZE_MB}MB /{' '}
          {ALIMTALK_CSV_LIMIT.MAX_ROW_COUNT.toLocaleString()}행
        </p>
      </Upload.Dragger>
      {isUploading && <Styled.UploadingStatus>업로드 중…</Styled.UploadingStatus>}
      {!isUploading && csvS3Key && (
        <Styled.Status>
          ✓ 업로드 완료
          {csvFileName ? ` · ${csvFileName}` : ''}
        </Styled.Status>
      )}
    </Styled.Section>
  );
};
